import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import * as THREE from 'three';
import { GameScene } from '../game/GameScene';
import { GameHUD } from '../game/hud/GameHUD';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { useAuthStore } from '../stores/authStore';
import { useGameStore } from '../stores/gameStore';
import { NetworkManager } from '../game/systems/NetworkManager';
import { InputManager } from '../game/systems/InputManager';
import { predictTick, reconcile, ensureLocalShip, getAsteroidPositions, getRemoteShipPredictedStates, getExtrapolatedMoonAngle, destroyClientPhysics, syncRemoteShips, saveAsteroidSyncState, restoreAsteroidSyncState } from '../game/systems/ClientPrediction';
import { initFrameTimingBuffer, recordFrame, getLiveFrameMetrics } from '../game/systems/FrameTimingBuffer';
import { initReconciliationLog } from '../game/systems/ReconciliationLog';
import { initAsteroidSyncLog } from '../game/systems/AsteroidSyncLog';
import { TICK_RATE } from '@warmancer/shared';
import { DebugPanel } from '../game/hud/DebugPanel';
import { SettingsPanel } from '../game/hud/SettingsPanel';
import { ParticleEditor } from '../game/ui/ParticleEditor';
import { usePostprocessingStore, validateSettings, CLIENT_VERSION } from '../stores/postprocessingStore';
import type { PostprocessingSettings } from '../stores/postprocessingStore';
import { useParticleStore } from '../stores/particleStore';
import type { ParticleSettings } from '../stores/particleStore';
import { validateEnvelope } from '../stores/settingsExport';
import { settingsApi, particleDefaultsApi } from '../api/client';
import { useParticleEffectStore } from '../stores/particleEffectStore';
import { reset as resetProjectiles } from '../game/effects/ProjectileSystem';
import styles from './Game.module.css';

// API base URL - use ws:// for local dev, wss:// for production
const API_WS_URL = import.meta.env.DEV
  ? 'ws://localhost:8788'
  : 'wss://api.warmancer.com';

function LoadingScreen() {
  return (
    <div className={styles.loading}>
      Loading...
    </div>
  );
}

function ErrorScreen() {
  return (
    <div className={styles.loading}>
      WebGL is not supported or failed to initialize.
    </div>
  );
}

export function Game() {
  const sessionToken = useAuthStore((s) => s.sessionToken);
  const reset = useGameStore((s) => s.reset);

  const networkRef = useRef<NetworkManager | null>(null);
  const inputRef = useRef<InputManager | null>(null);
  const tickRef = useRef<number | null>(null);
  const clientTickRef = useRef(0);
  const lastSnapshotTickRef = useRef(0);
  const [debugWireframes, setDebugWireframes] = useState(false);
  const [settingsVisible, setSettingsVisible] = useState(false);
  const [particleEditorVisible, setParticleEditorVisible] = useState(false);
  const [clientTickDisplay, setClientTickDisplay] = useState(0);
  const [fpsDisplay, setFpsDisplay] = useState(0);

  // Suppress game input while settings panel is open
  useEffect(() => {
    inputRef.current?.setSuppressed(settingsVisible);
  }, [settingsVisible]);

  // Load settings from server on mount (non-blocking)
  // Handles both new unified envelope format ({ postprocessing, particles })
  // and legacy bare-postprocessing format (has bloom at root).
  useEffect(() => {
    if (!sessionToken) return;
    let cancelled = false;

    settingsApi.load().then((result) => {
      if (cancelled) return;
      if (result?.data) {
        const data = result.data;
        const settings = data.settings as Record<string, unknown> | undefined;
        if (!settings) return;

        // Try unified envelope format first
        const envelopeError = validateEnvelope(settings);
        if (!envelopeError) {
          const envelope = settings as unknown as { postprocessing: PostprocessingSettings; particles: ParticleSettings };
          usePostprocessingStore.getState().applySettings(envelope.postprocessing);
          useParticleStore.getState().applySettings(envelope.particles);
          return;
        }

        // Fall back to legacy bare-postprocessing format
        const ppError = validateSettings(settings);
        if (!ppError) {
          usePostprocessingStore.getState().applySettings(settings as unknown as PostprocessingSettings);
        }
      }
    }).catch(() => {
      // Non-blocking: settings load failure doesn't block game
    });

    return () => { cancelled = true; };
  }, [sessionToken]);

  // Auto-save settings to server with 2-second debounce.
  // Subscribes to both postprocessing and particle stores, saves unified envelope.
  useEffect(() => {
    if (!sessionToken) return;

    let saveTimer: ReturnType<typeof setTimeout> | null = null;

    function scheduleSave() {
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = setTimeout(() => {
        const envelope = {
          postprocessing: usePostprocessingStore.getState().settings,
          particles: useParticleStore.getState().settings,
        };
        settingsApi.save(
          envelope as unknown as Record<string, unknown>,
          CLIENT_VERSION,
        ).catch(() => {
          // Non-blocking: save failure is silent
        });
      }, 2000);
    }

    const unsubscribePP = usePostprocessingStore.subscribe((state, prevState) => {
      if (state.settings === prevState.settings) return;
      scheduleSave();
    });

    const unsubscribeParticles = useParticleStore.subscribe((state, prevState) => {
      if (state.settings === prevState.settings) return;
      scheduleSave();
    });

    return () => {
      unsubscribePP();
      unsubscribeParticles();
      if (saveTimer) clearTimeout(saveTimer);
    };
  }, [sessionToken]);

  // Load particle effect defaults from server on mount (non-blocking)
  const fetchParticleDefaults = useCallback(() => {
    particleDefaultsApi.load().then((result) => {
      if (result?.defaults && typeof result.defaults === 'object') {
        const defaults = result.defaults;
        if (Object.keys(defaults).length > 0) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          useParticleEffectStore.getState().setServerDefaults(defaults as any);
          useParticleEffectStore.getState().applyServerDefaults();
        }
      }
    }).catch(() => {
      // Non-blocking: particle defaults load failure doesn't block game
    });
  }, []);

  useEffect(() => {
    fetchParticleDefaults();
  }, [fetchParticleDefaults]);

  // Connect to game server
  useEffect(() => {
    if (!sessionToken) return;

    const network = new NetworkManager();
    const input = new InputManager();
    networkRef.current = network;
    inputRef.current = input;

    network.connect(API_WS_URL, sessionToken);

    // Wire up particle defaults broadcast callback
    network.setOnParticleDefaultsUpdated(fetchParticleDefaults);

    // Wait for connection before starting input
    const checkConnection = setInterval(() => {
      const ws = network.getWebSocket();
      if (ws && ws.readyState === WebSocket.OPEN) {
        clearInterval(checkConnection);
        input.start(ws);
        startGameLoop(input);
      }
    }, 100);

    return () => {
      clearInterval(checkConnection);
      stopGameLoop();
      input.stop();
      network.disconnect();
      destroyClientPhysics();
      resetProjectiles();
      reset();
    };
  }, [sessionToken]);

  // ---------------------------------------------------------------------------
  // GAME LOOP ARCHITECTURE — READ BEFORE MODIFYING
  //
  // WARNING: This game loop was debugged across Sessions 83-86. Every line
  // exists because a previous version caused visible bugs. Do NOT change the
  // timer mechanism, accumulator logic, or epsilon nudge without understanding
  // the full history documented below.
  //
  // WHY requestAnimationFrame (NOT setInterval):
  //   setInterval(1000/60) fires at ~58.8Hz on Firefox (not 60Hz). The time
  //   accumulator catches up by running 2 physics ticks in one callback every
  //   ~50 frames. Those 2-tick bursts cause:
  //     - Ship/asteroid positions jump 2x the normal distance
  //     - Render lerp covers 83.5% of the doubled gap in one frame — visible spike
  //     - Moon (which had no lerp) jumps directly by 2x
  //   Result: periodic jitter ~1.2 times per second on Firefox, invisible on Chrome.
  //   rAF fires at the display's vsync rate with sub-millisecond variance on
  //   BOTH browsers, eliminating the problem entirely.
  //
  // WHY epsilon nudge (NOT Math.max guarantee):
  //   The previous fix (Session 84) used:
  //     accumulatorRef.current = Math.max(accumulatorRef.current, TICK_DELTA_MS)
  //   This forced at least 1 tick per callback unconditionally. On a 120Hz
  //   monitor, rAF fires every ~8.33ms, so Math.max would force 120 physics
  //   ticks/sec instead of 60 — broken.
  //   The epsilon nudge only fires when the accumulator is within 0.5ms of a
  //   full tick (floating-point rounding + Firefox rAF variance), which happens
  //   at 60Hz (~16.17-16.67ms) but NOT at 120Hz (8.33ms << 16.17ms).
  //
  // ACCUMULATOR RULES (DO NOT CHANGE):
  //   1. Snapshot frames: acc = TICK_DELTA_MS (exactly 1 tick after reconciliation)
  //   2. Non-snapshot frames: acc += elapsed
  //   3. Cap: Math.min(acc, MAX_TICKS_PER_FRAME * TICK_DELTA_MS) — spiral of death
  //   4. Epsilon: if (acc > TICK_DELTA_MS - 0.5) acc = max(acc, TICK_DELTA_MS)
  //   5. While: while (acc >= TICK_DELTA_MS) { acc -= TICK_DELTA_MS; tick(); }
  //
  // EARLY RETURNS MUST SCHEDULE NEXT FRAME:
  //   Every early return path (localEntityId null, localShip null, first frame)
  //   must call requestAnimationFrame(loop) before returning. Otherwise the
  //   loop stops entirely.
  //
  // PREVIOUS BUGS FIXED BY THIS CODE:
  //   Session 83: Firefox asteroid drift (setInterval ~58.8Hz → accumulator)
  //   Session 84: Chrome 0-tick stutter (accumulator < 16.67ms → Math.max)
  //   Session 85: Moon frozen at (80,0), tick drift after DO eviction
  //   Session 86: Firefox periodic jitter (setInterval → rAF + epsilon nudge)
  //
  // TEST COVERAGE: gameSimulation.test.ts mirrors this accumulator logic.
  //   asteroidDesync.test.ts has structural tests verifying rAF, epsilon, etc.
  //   Any change here MUST be mirrored in tests or they will fail.
  // ---------------------------------------------------------------------------
  function startGameLoop(input: InputManager) {
    initFrameTimingBuffer();
    initReconciliationLog();
    initAsteroidSyncLog();

    const clientTickInitialized = { current: false };
    const worldSetupEpochRef = { current: 0 };
    const TICK_DELTA_MS = 1000 / TICK_RATE;
    const MAX_TICKS_PER_FRAME = 4;
    const ACCUMULATOR_EPSILON_MS = 0.5; // Covers 60Hz Firefox rAF variance (+-0.5ms). Safe for 120Hz+ (8.33ms << 16.17ms threshold).
    const SNAPSHOT_WATCHDOG_MS = 5000;

    const lastTimeRef = { current: 0 };
    const accumulatorRef = { current: 0 };
    const projectionSetUp = { current: false };

    // Reusable objects for mouse-to-world raycasting (no per-frame allocation)
    const raycaster = new THREE.Raycaster();
    const ndcCoords = new THREE.Vector2();
    const yPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    const intersection = new THREE.Vector3();

    // FPS tracking: count rAF callbacks per second
    const fpsCountRef = { current: 0 };
    const fpsLastSecRef = { current: 0 };

    const loop = (timestamp: DOMHighResTimeStamp) => {
      // First frame: initialize timing refs and schedule next frame
      if (lastTimeRef.current === 0) {
        lastTimeRef.current = timestamp;
        fpsLastSecRef.current = timestamp;
        tickRef.current = requestAnimationFrame(loop);
        return;
      }

      const elapsed = timestamp - lastTimeRef.current;
      lastTimeRef.current = timestamp;

      // FPS counter: count frames, emit once per second
      fpsCountRef.current++;
      if (timestamp - fpsLastSecRef.current >= 1000) {
        setFpsDisplay(fpsCountRef.current);
        fpsCountRef.current = 0;
        fpsLastSecRef.current = timestamp;
      }

      const state = useGameStore.getState();

      // Initialize clientTick from server tick (once, when WorldSetup arrives).
      // Also re-initialize when worldSetupEpoch changes (monotonically increasing
      // counter). Previous approach compared serverTick, which fails when a new DO
      // starts at tick 0 — same value as the initial connection, so the condition
      // `0 !== 0` is false and reconnection logic never fires.
      if (state.worldSetup) {
        const currentEpoch = state.worldSetupEpoch;
        if (!clientTickInitialized.current || currentEpoch !== worldSetupEpochRef.current) {
          clientTickRef.current = state.worldSetup.serverTick;
          lastSnapshotTickRef.current = 0;
          worldSetupEpochRef.current = currentEpoch;
          clientTickInitialized.current = true;

          // Reset input pipeline for clean reconnection. Without this, stale
          // pendingInputs and a high inputSequence from the old connection cause
          // permanent input deadlock (server expects seq 1, client sends seq 63766+).
          useGameStore.getState().clearPendingInputs();

          // Update InputManager's WebSocket reference. On reconnection,
          // NetworkManager creates a new WebSocket but InputManager still holds
          // the old closed one, causing tick() to return null (readyState !== OPEN).
          const ws = networkRef.current?.getWebSocket();
          if (ws && ws.readyState === WebSocket.OPEN) {
            input.updateWebSocket(ws);
          }
        }
      }

      // Snapshot watchdog: if connected and receiving snapshots but they stop
      // for SNAPSHOT_WATCHDOG_MS, the DO likely hibernated and lost our session.
      // Force a reconnect so the client gets a fresh WorldSetup.
      if (
        state.connectionStatus === 'connected' &&
        state.lastSnapshotReceivedAt > 0 &&
        performance.now() - state.lastSnapshotReceivedAt > SNAPSHOT_WATCHDOG_MS
      ) {
        networkRef.current?.forceReconnect();
        tickRef.current = requestAnimationFrame(loop);
        return;
      }

      // Resync clientTick from server on each snapshot BEFORE running ticks.
      // Without resync, clientTick drifts unboundedly over time,
      // causing the physics moon in ClientPrediction to be at the wrong position.
      // Resyncing bounds max drift to TICKS_PER_NETWORK (~3 ticks).
      const newSnapshot = state.serverTick !== lastSnapshotTickRef.current;
      if (newSnapshot) {
        clientTickRef.current = state.serverTick;
        lastSnapshotTickRef.current = state.serverTick;
      }

      // Early returns: no inputs are sent during bootstrap. The server handles
      // missing inputs gracefully (hold-previous model), and sending inputs
      // without a ship entity is pointless.
      if (state.localEntityId === null) {
        tickRef.current = requestAnimationFrame(loop);
        return;
      }

      // Bootstrap: if localShip is null, initialize from serverShip
      if (!state.localShip) {
        if (state.serverShip) {
          useGameStore.getState().setLocalShip(state.serverShip);
          useGameStore.getState().clearServerShip();
        }
        tickRef.current = requestAnimationFrame(loop);
        return;
      }

      // Ensure ship body exists in client physics world
      ensureLocalShip(state.localEntityId, state.localShip.x, state.localShip.y, state.localShip.rotation);

      // Set up mouse-to-world projection once camera is available
      if (!projectionSetUp.current) {
        const camera = useGameStore.getState().cameraRef;
        if (camera) {
          input.setWorldProjection((sx: number, sy: number) => {
            // Convert screen coordinates to NDC (-1 to +1)
            ndcCoords.set(
              (sx / window.innerWidth) * 2 - 1,
              -(sy / window.innerHeight) * 2 + 1,
            );
            raycaster.setFromCamera(ndcCoords, camera);
            raycaster.ray.intersectPlane(yPlane, intersection);
            // Three.js Z = game Y (2D physics uses X/Y, 3D uses X/Z)
            return { x: intersection.x, y: intersection.z };
          });
          projectionSetUp.current = true;
        }
      }

      // Update ship world position for targetAngle calculation
      input.setShipWorldPosition(state.localShip.x, state.localShip.y);

      // Frame timing: capture accumulator state before physics
      const accBefore = accumulatorRef.current;
      let ticksThisFrame = 0;
      let reconcileMs = 0;

      // Reconcile when a new server snapshot arrived (detected above)
      if (newSnapshot) {
        // Sync remote ship physics bodies BEFORE reconciliation.
        // This ensures remoteShipStates and remoteShipInputs have current snapshot
        // data when resetRemoteShips() and remote input application run inside reconcile().
        // Previously this was after reconcile, causing replay to use stale remote positions.
        syncRemoteShips(state.remotePlayers);

        if (state.serverShip && state.pendingInputs.length > 0) {
          const clientShipBefore = state.localShip;
          const reconcileStart = performance.now();
          const reconciled = reconcile(
            state.serverShip,
            state.pendingInputs,
            state.serverTick,
            {
              clientTickBefore: clientTickRef.current,
              clientShipBefore: state.localShip,
              lastAckedInput: state.lastAckedInput,
              reconcileStartMs: reconcileStart,
            },
          );
          reconcileMs = performance.now() - reconcileStart;

          // Measure correction distance: how far reconciliation moved the ship
          // from its predicted position. This is the actual prediction error.
          // Old metric measured dist(localShip, serverShip) = speed * latency (wrong).
          if (clientShipBefore) {
            const dx = reconciled.x - clientShipBefore.x;
            const dy = reconciled.y - clientShipBefore.y;
            const correctionDist = Math.sqrt(dx * dx + dy * dy);
            useGameStore.getState().setDesyncDistance(correctionDist);
          }

          useGameStore.getState().setLocalShip(reconciled);
          // After reconciliation replays N pending inputs, the ship is at state-after
          // tick serverTick+N. Set clientTick to match so predictTick() extrapolates
          // the moon to the correct position for the next step (serverTick+N+1).
          // Without this, the moon is at serverTick+1 instead of serverTick+N+1,
          // causing wrong gravity for ALL bodies and divergence from server.
          clientTickRef.current = state.serverTick + state.pendingInputs.length;
        } else if (state.serverShip) {
          // No pending inputs — accept server state directly
          useGameStore.getState().setLocalShip(state.serverShip);
        }
        useGameStore.getState().clearServerShip();

        // Save asteroid positions BEFORE the forced prediction tick. The forced tick
        // advances ALL bodies including asteroids, putting them 1 tick ahead of the
        // server (client: 0+1+3=4 ticks vs server: 3 ticks between syncs). Saving
        // here and restoring after the tick loop eliminates the 1-tick offset that
        // causes 20Hz jitter on moving asteroids. Ship collision response during the
        // tick is preserved — only asteroid positions are rolled back.
        saveAsteroidSyncState();

        // After reconciliation, set accumulator to exactly 1 tick. This ensures
        // exactly 1 physics step runs after reconciliation (the "current frame" tick).
        // Prevents double-counting: elapsed time that included the snapshot delivery
        // is consumed by reconciliation, not added to the accumulator.
        accumulatorRef.current = TICK_DELTA_MS;
      } else {
        // Non-snapshot frame: accumulate real elapsed time
        accumulatorRef.current += elapsed;
      }

      // Cap accumulator to prevent spiral of death (e.g., returning from background tab).
      // MAX_TICKS_PER_FRAME=4 allows catch-up for minor timer drift while preventing
      // runaway physics if the tab was backgrounded for seconds.
      accumulatorRef.current = Math.min(accumulatorRef.current, MAX_TICKS_PER_FRAME * TICK_DELTA_MS);

      // Epsilon nudge: handle floating-point rounding at the 60Hz vsync boundary.
      // rAF at 60Hz delivers ~16.67ms, but floating-point can produce 16.66ms.
      // When the accumulator is within ACCUMULATOR_EPSILON_MS (0.5ms) of a full tick,
      // nudge it up so the tick fires. This does NOT affect 120Hz/144Hz frames
      // (8.33ms is far below 16.57ms threshold), so physics stays at 60Hz.
      if (accumulatorRef.current > TICK_DELTA_MS - ACCUMULATOR_EPSILON_MS) {
        accumulatorRef.current = Math.max(accumulatorRef.current, TICK_DELTA_MS);
      }

      // Run physics ticks based on accumulated time. On 120Hz displays, most
      // frames accumulate ~8.33ms (0 ticks), and every other frame accumulates
      // ~16.67ms (1 tick). Over any time window, this produces ~60 steps/sec.
      while (accumulatorRef.current >= TICK_DELTA_MS) {
        accumulatorRef.current -= TICK_DELTA_MS;
        ticksThisFrame++;

        // Increment tick and send input AFTER reconciliation so the tick value
        // reflects the correct post-reconciliation state (serverTick + pendingInputs.length + 1).
        // Before this fix, input.tick() ran before reconciliation on snapshot frames,
        // causing inputs to be sent with tick = serverTick + 1 instead of the correct value,
        // producing bizarre tick ordering in pending inputs.
        clientTickRef.current++;
        const frame = input.tick(clientTickRef.current);

        // Run one prediction tick for the current frame's input
        if (frame) {
          const currentShip = useGameStore.getState().localShip;
          if (currentShip) {
            const predicted = predictTick(currentShip, frame.bitfield, clientTickRef.current, frame.targetAngle);
            useGameStore.getState().setLocalShip(predicted);
          }
        }
      }

      // Restore asteroid positions after the tick loop on snapshot frames.
      // The forced prediction tick advanced asteroids by 1 tick beyond synced state.
      // Restoring eliminates the 1-tick-ahead offset: 0 (restored) + 3 (natural) = 3
      // ticks between syncs = matches server exactly. Ship collision response from the
      // forced tick is already baked into ship velocity and preserved.
      if (newSnapshot) {
        restoreAsteroidSyncState();
      }

      // Compute sub-tick interpolation alpha for velocity-based extrapolation.
      // Render components use this to extrapolate visual positions between physics ticks,
      // producing constant visual velocity regardless of frame rate or timing variance.
      // This replaces the old exponential chase (RENDER_LERP_SPEED * delta) which produced
      // variable visual velocity between ticks, causing stutter on Firefox with variable FPS.
      const renderAlpha = accumulatorRef.current / TICK_DELTA_MS;
      useGameStore.getState().setRenderAlpha(renderAlpha);

      // Record frame timing for diagnostics
      recordFrame(timestamp, elapsed, accBefore, accumulatorRef.current, ticksThisFrame, renderAlpha, newSnapshot, reconcileMs);

      // Update moon visual angle, asteroid positions, and remote ship predicted
      // positions ONCE after all ticks. Only the final value matters for rendering
      // (the intermediate values are never seen).
      // Moon angle is extrapolated from the server snapshot's authoritative moonAngle
      // at 60Hz for smooth rendering (sole writer — updateFromSnapshot does NOT write it).
      useGameStore.getState().setMoonAngle(getExtrapolatedMoonAngle(clientTickRef.current));
      useGameStore.getState().setAsteroidPositions(getAsteroidPositions());
      useGameStore.getState().setRemotePredictedPositions(getRemoteShipPredictedStates());

      // Write diagnostic ticks to store for debug export
      useGameStore.getState().setClientTick(clientTickRef.current);
      useGameStore.getState().setLastSnapshotTick(lastSnapshotTickRef.current);

      // Update debug display at ~4Hz (every 15 ticks)
      if (clientTickRef.current % 15 === 0) {
        setClientTickDisplay(clientTickRef.current);
        useGameStore.getState().setLiveFrameMetrics(getLiveFrameMetrics());
      }

      // Schedule next frame
      tickRef.current = requestAnimationFrame(loop);
    };

    tickRef.current = requestAnimationFrame(loop);
  }

  function stopGameLoop() {
    if (tickRef.current !== null) {
      cancelAnimationFrame(tickRef.current);
      tickRef.current = null;
    }
  }

  const toggleWireframes = useCallback(() => {
    setDebugWireframes((v) => !v);
  }, []);

  const closeSettings = useCallback(() => {
    setSettingsVisible(false);
  }, []);

  const openParticleEditor = useCallback(() => {
    setParticleEditorVisible(true);
  }, []);

  const closeParticleEditor = useCallback(() => {
    setParticleEditorVisible(false);
  }, []);

  const broadcastParticleUpdate = useCallback(() => {
    networkRef.current?.requestParticleDefaultsBroadcast();
  }, []);

  // Escape key toggles settings panel (when settings is closed)
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && !settingsVisible) {
        e.preventDefault();
        setSettingsVisible(true);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [settingsVisible]);

  return (
    <div className={styles.container}>
      {/* Canvas area: always full width (settings panel overlays, doesn't resize) */}
      <div className={styles.canvasArea}>
        {/* Game HUD */}
        <GameHUD fps={fpsDisplay} />

        {/* Debug Panel */}
        <DebugPanel
          debugWireframes={debugWireframes}
          onToggleWireframes={toggleWireframes}
          clientTick={clientTickDisplay}
          fps={fpsDisplay}
        />

        {/* 3D Canvas */}
        <ErrorBoundary fallback={<ErrorScreen />}>
          <Suspense fallback={<LoadingScreen />}>
            <Canvas className={styles.canvas} gl={{ powerPreference: 'high-performance', antialias: false, stencil: false, depth: false }}>
              <GameScene debugWireframes={debugWireframes} />
            </Canvas>
          </Suspense>
        </ErrorBoundary>
      </div>

      {/* Settings Panel — fixed overlay on right side, no canvas resize */}
      <SettingsPanel visible={settingsVisible} onClose={closeSettings} onOpenParticleEditor={openParticleEditor} />

      {/* Particle Editor — full-screen overlay */}
      <ParticleEditor visible={particleEditorVisible} onClose={closeParticleEditor} onBroadcastParticleUpdate={broadcastParticleUpdate} />
    </div>
  );
}
